/**
 * NotificationBadge Component
 * FindMyLawyer – Lawyer Portal
 *
 * Bell icon with:
 * - Animated red badge showing unread count
 * - Dropdown preview grouped as: Today / Yesterday / Earlier
 * - "View All" button to navigate to full page
 * - "Mark All Read" action
 */

import React, { useEffect, useRef, useState } from "react";
import {
  Bell, BellRing, Bot, Calendar, CheckCircle, FileText,
  Headphones, IndianRupee, Info, MessageCircle, ShieldCheck,
  Star, Video, Wallet, XCircle, Check,
} from "lucide-react";
import { useNotifications } from "../context/NotificationContext";
import { getNotificationConfig } from "../utils/notificationConfig";

// ── Icon resolver ─────────────────────────────────────────────────────────────
const ICON_MAP = {
  Bell, Bot, Calendar, CheckCircle, FileText, Headphones,
  IndianRupee, Info, MessageCircle, ShieldCheck, Star,
  Video, Wallet, XCircle,
};
function NotifIcon({ name, size = 14 }) {
  const Comp = ICON_MAP[name] || Bell;
  return <Comp size={size} />;
}

// ── Relative time ──────────────────────────────────────────────────────────────
function relativeTime(dateStr) {
  const diff = Math.floor((Date.now() - new Date(dateStr).getTime()) / 1000);
  if (diff < 60)    return "Just now";
  if (diff < 3600)  return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return new Date(dateStr).toLocaleDateString("en-IN", { day: "numeric", month: "short" });
}

// ── Date group helper ─────────────────────────────────────────────────────────
function getDateGroup(dateStr) {
  const d   = new Date(dateStr);
  const now = new Date();
  const todayStart     = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const yesterdayStart = new Date(todayStart.getTime() - 86400000);

  if (d >= todayStart)     return "Today";
  if (d >= yesterdayStart) return "Yesterday";
  return "Earlier";
}

function groupNotifications(notifications) {
  const groups = { Today: [], Yesterday: [], Earlier: [] };
  for (const n of notifications) {
    const g = getDateGroup(n.created_at);
    groups[g].push(n);
  }
  return groups;
}

// ── NotificationBadge ─────────────────────────────────────────────────────────
export const NotificationBadge = ({ onNavigate }) => {
  const { unreadCount, notifications, markRead, markAllRead } = useNotifications();
  const [open, setOpen]   = useState(false);
  const dropdownRef        = useRef(null);
  const buttonRef          = useRef(null);

  // Close on outside click
  useEffect(() => {
    const handler = (e) => {
      if (
        dropdownRef.current && !dropdownRef.current.contains(e.target) &&
        buttonRef.current   && !buttonRef.current.contains(e.target)
      ) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  // Latest 5 unread (or just latest 5)
  const preview = notifications.slice(0, 5);
  const grouped = groupNotifications(preview);
  const hasUnread = unreadCount > 0;

  const handleViewAll = () => {
    setOpen(false);
    onNavigate && onNavigate("notifications");
  };

  return (
    <div style={{ position: "relative" }}>
      {/* Bell button */}
      <button
        ref={buttonRef}
        onClick={() => setOpen((v) => !v)}
        style={{
          width:          "40px",
          height:         "40px",
          borderRadius:   "11px",
          border:         `1px solid ${open ? "#3b82f6" : "#e2e8f0"}`,
          background:     open ? "#eff6ff" : "#ffffff",
          color:          open ? "#3b82f6" : "#475569",
          display:        "flex",
          alignItems:     "center",
          justifyContent: "center",
          position:       "relative",
          transition:     "all 0.2s ease",
          cursor:         "pointer",
        }}
        title="Notifications"
        aria-label={`Notifications (${unreadCount} unread)`}
        id="notification-bell-btn"
      >
        {hasUnread ? <BellRing size={17} /> : <Bell size={17} />}

        {/* Badge */}
        {unreadCount > 0 && (
          <span style={{
            position:       "absolute",
            top:            "-4px",
            right:          "-4px",
            minWidth:       "18px",
            height:         "18px",
            borderRadius:   "9px",
            background:     "#ef4444",
            color:          "#fff",
            fontSize:       "10px",
            fontWeight:     700,
            display:        "flex",
            alignItems:     "center",
            justifyContent: "center",
            padding:        "0 4px",
            border:         "2px solid #ffffff",
            animation:      hasUnread ? "bellPulse 2s ease-in-out infinite" : "none",
          }}>
            {unreadCount > 99 ? "99+" : unreadCount}
          </span>
        )}
      </button>

      {/* Dropdown */}
      {open && (
        <div
          ref={dropdownRef}
          id="notification-dropdown"
          style={{
            position:     "absolute",
            top:          "calc(100% + 8px)",
            right:        0,
            width:        "360px",
            maxHeight:    "480px",
            background:   "#ffffff",
            border:       "1px solid #e2e8f0",
            borderRadius: "16px",
            boxShadow:    "0 20px 60px rgba(15, 23, 42, 0.15), 0 4px 16px rgba(15, 23, 42, 0.08)",
            zIndex:       50,
            overflow:     "hidden",
            display:      "flex",
            flexDirection: "column",
            animation:    "dropdownIn 0.18s ease-out",
          }}
        >
          {/* Header */}
          <div style={{
            padding:        "14px 16px 10px",
            borderBottom:   "1px solid #f1f5f9",
            display:        "flex",
            alignItems:     "center",
            justifyContent: "space-between",
          }}>
            <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
              <div style={{
                width:          "28px",
                height:         "28px",
                borderRadius:   "8px",
                background:     "#eff6ff",
                color:          "#3b82f6",
                display:        "flex",
                alignItems:     "center",
                justifyContent: "center",
              }}>
                <Bell size={14} />
              </div>
              <span style={{ fontWeight: 700, fontSize: "13.5px", color: "#0f172a" }}>
                Notifications
              </span>
              {unreadCount > 0 && (
                <span style={{
                  background:   "#eff6ff",
                  color:        "#3b82f6",
                  borderRadius: "6px",
                  fontSize:     "11px",
                  fontWeight:   700,
                  padding:      "1px 6px",
                }}>
                  {unreadCount} unread
                </span>
              )}
            </div>

            {unreadCount > 0 && (
              <button
                onClick={() => markAllRead()}
                style={{
                  fontSize:     "11.5px",
                  color:        "#3b82f6",
                  fontWeight:   600,
                  cursor:       "pointer",
                  background:   "none",
                  border:       "none",
                  display:      "flex",
                  alignItems:   "center",
                  gap:          "4px",
                }}
              >
                <Check size={12} /> Mark all read
              </button>
            )}
          </div>

          {/* Notification list grouped */}
          <div style={{ overflowY: "auto", flex: 1 }}>
            {preview.length === 0 ? (
              <div style={{
                display:        "flex",
                flexDirection:  "column",
                alignItems:     "center",
                justifyContent: "center",
                padding:        "32px 16px",
                color:          "#94a3b8",
              }}>
                <span style={{ fontSize: "28px", marginBottom: "8px" }}>🎉</span>
                <p style={{ fontWeight: 600, fontSize: "13px", color: "#64748b" }}>You're all caught up!</p>
                <p style={{ fontSize: "12px", marginTop: "4px" }}>No notifications right now.</p>
              </div>
            ) : (
              ["Today", "Yesterday", "Earlier"].map((group) => {
                const items = grouped[group];
                if (!items || items.length === 0) return null;

                return (
                  <div key={group}>
                    <div style={{
                      padding:     "8px 16px 4px",
                      fontSize:    "10.5px",
                      fontWeight:  700,
                      color:       "#94a3b8",
                      textTransform: "uppercase",
                      letterSpacing: "0.05em",
                      background:  "#f8fafc",
                    }}>
                      {group}
                    </div>

                    {items.map((n) => {
                      const cfg     = getNotificationConfig(n.type);
                      const isUnread = n.status === "UNREAD";

                      return (
                        <div
                          key={n.id}
                          onClick={() => {
                            if (isUnread) markRead(n.id);
                          }}
                          style={{
                            display:     "flex",
                            gap:         "10px",
                            padding:     "10px 16px",
                            borderBottom: "1px solid #f1f5f9",
                            background:   isUnread ? "#fafbff" : "#ffffff",
                            cursor:       "pointer",
                            transition:   "background 0.15s",
                            borderLeft:   isUnread ? `3px solid ${cfg.color}` : "3px solid transparent",
                          }}
                          onMouseEnter={(e) => { e.currentTarget.style.background = "#f8fafc"; }}
                          onMouseLeave={(e) => { e.currentTarget.style.background = isUnread ? "#fafbff" : "#ffffff"; }}
                        >
                          <div style={{
                            width:          "32px",
                            height:         "32px",
                            borderRadius:   "8px",
                            background:     cfg.bgColor,
                            color:          cfg.color,
                            display:        "flex",
                            alignItems:     "center",
                            justifyContent: "center",
                            flexShrink:     0,
                          }}>
                            <NotifIcon name={cfg.icon} size={14} />
                          </div>

                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                              <p style={{
                                fontWeight:   isUnread ? 700 : 500,
                                fontSize:     "12px",
                                color:        "#0f172a",
                                overflow:     "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace:   "nowrap",
                                maxWidth:     "200px",
                              }}>
                                {n.title}
                              </p>
                              <span style={{ fontSize: "10.5px", color: "#94a3b8", flexShrink: 0 }}>
                                {relativeTime(n.created_at)}
                              </span>
                            </div>
                            <p style={{
                              fontSize:     "11.5px",
                              color:        "#64748b",
                              marginTop:    "2px",
                              overflow:     "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace:   "nowrap",
                            }}>
                              {n.message}
                            </p>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                );
              })
            )}
          </div>

          {/* Footer */}
          <div style={{
            borderTop:  "1px solid #f1f5f9",
            padding:    "10px 16px",
          }}>
            <button
              onClick={handleViewAll}
              style={{
                width:          "100%",
                padding:        "8px",
                borderRadius:   "9px",
                background:     "#0f172a",
                color:          "#ffffff",
                fontSize:       "12.5px",
                fontWeight:     600,
                border:         "none",
                cursor:         "pointer",
                transition:     "opacity 0.15s",
              }}
              onMouseEnter={(e) => { e.currentTarget.style.opacity = "0.85"; }}
              onMouseLeave={(e) => { e.currentTarget.style.opacity = "1"; }}
              id="view-all-notifications-btn"
            >
              View All Notifications
            </button>
          </div>
        </div>
      )}

      <style>{`
        @keyframes bellPulse {
          0%, 100% { transform: scale(1); }
          50%       { transform: scale(1.15); }
        }
        @keyframes dropdownIn {
          from { opacity: 0; transform: translateY(-8px) scale(0.97); }
          to   { opacity: 1; transform: translateY(0)  scale(1);    }
        }
      `}</style>
    </div>
  );
};
